import { forwardRef, useMemo } from 'react'
import { useInput, useRecordContext, InputHelperText, FieldTitle } from 'react-admin'
import { Box, TextField } from '@mui/material'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { ALLOWED_PHONE_COUNTRIES, isAllowedPhoneNumber, toE164 } from '../../utils/phone'

// react-phone-number-input needs the ref on the underlying <input>.
const MuiPhoneTextField = forwardRef((props, ref) => <TextField {...props} inputRef={ref} />)
MuiPhoneTextField.displayName = 'MuiPhoneTextField'

const PHONE_ERROR = 'Please enter a valid phone number'

/**
 * react-admin phone input with a country picker. Stores E.164 (e.g. +923044996508).
 * Only validates the number when it differs from the saved record, matching the API, so a
 * legacy malformed number doesn't block saving unrelated edits.
 */
const PhoneNumberInput = ({ source, label, validate, fullWidth, sx, ...rest }) => {
  const record = useRecordContext()
  const originalValue = record?.[source]

  const validators = useMemo(() => {
    const phoneValidator = (value) => {
      if (!value || value === originalValue) return undefined
      return isAllowedPhoneNumber(value) ? undefined : PHONE_ERROR
    }
    return [phoneValidator, ...(Array.isArray(validate) ? validate : validate ? [validate] : [])]
  }, [validate, originalValue])

  const {
    id,
    field,
    fieldState: { error, isTouched },
    formState: { isSubmitted },
    isRequired,
  } = useInput({ source, validate: validators, ...rest })

  const showError = !!error && (isTouched || isSubmitted)

  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 'fit-content',
        '& .PhoneInput': { alignItems: 'flex-start' },
        '& .PhoneInputCountry': { mt: '-18px', mr: 1 },
        ...sx,
      }}
    >
      <PhoneInput
        id={id}
        defaultCountry="US"
        countries={ALLOWED_PHONE_COUNTRIES}
        addInternationalOption={false}
        inputComponent={MuiPhoneTextField}
        value={toE164(field.value)}
        onChange={(next) => field.onChange(next || '')}
        onBlur={field.onBlur}
        label={<FieldTitle label={label} source={source} isRequired={isRequired} />}
        error={showError}
        helperText={<InputHelperText touched={showError} error={error?.message} />}
        fullWidth={fullWidth}
      />
    </Box>
  )
}

export default PhoneNumberInput
